import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { getMaxDepartureDate, getMinDepartureDate } from '@vakantiesnl/services/src/util/dateUtils';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import type flatpickr from 'flatpickr';
import { Dutch } from 'flatpickr/dist/l10n/nl.js';
import { renderToString } from 'react-dom/server';
import { type DateTimePickerProps } from 'react-flatpickr';

export const getWeekNumber = (givenDate: Date): string => {
	dayjs.extend(weekOfYear);
	return dayjs(givenDate).week().toLocaleString('nl-NL', { minimumIntegerDigits: 2 });
};

export function getDatePickerOptions(
	options?: DateTimePickerProps['options'],
	calendarMode?: flatpickr.Options.Options['mode'],
): DateTimePickerProps['options'] {
	return {
		weekNumbers: true,
		locale: Dutch,
		dateFormat: 'Y-m-d',
		altFormat: 'Y-m-d',
		ariaDateFormat: 'Y-m-d',
		animate: false,
		minDate: getMinDepartureDate().toDate(),
		maxDate: getMaxDepartureDate().toDate(),
		mode: calendarMode,
		plugins: [staticYearMonthPlugin()],
		position: 'below',
		monthSelectorType: 'static',
		allowInput: false,
		disableMobile: true,
		clickOpens: true,
		altInputClass: 'input',
		getWeek: getWeekNumber,
		inline: true,
		prevArrow: renderToString(<Icon type="chevronLeft" />),
		nextArrow: renderToString(<Icon type="chevronRight" />),
		...options,
	};
}

type Locale = flatpickr.Locale;
type Plugin = flatpickr.Options.Plugin;
type Options = flatpickr.Options.Options;
type Instance = flatpickr.Instance;

export const monthToStr = (monthNumber: number, shorthand: boolean, locale: Locale): string =>
	locale.months[shorthand ? 'shorthand' : 'longhand'][monthNumber];

export function staticYearMonthPlugin(): Plugin {
	return (fp: Instance): Options => {
		function staticYearMonth(): void {
			if (!fp.monthNav) return;

			const selector = fp.monthNav.querySelector('.flatpickr-current-month .cur-month');
			if (selector && selector.parentNode) {
				selector.parentNode.removeChild(selector);
			}
			const selectorInput = fp.monthNav.querySelector('.flatpickr-current-month .numInputWrapper');
			if (selectorInput && selectorInput.parentNode) {
				selectorInput.parentNode.removeChild(selectorInput);
			}
			const monthElement = window.document.createElement('span');
			monthElement.className = 'cur-month';
			monthElement.textContent =
				monthToStr(fp.currentMonth, fp.config.shorthandCurrentMonth, fp.l10n) + ' ' + fp.currentYear;
			const monthItem = fp.monthNav.querySelector('.flatpickr-current-month');
			if (monthItem) {
				monthItem.appendChild(monthElement);
			}
		}

		return {
			onOpen: staticYearMonth,
			onMonthChange: staticYearMonth,
			onYearChange: staticYearMonth,
			onChange: staticYearMonth,
			onValueUpdate: staticYearMonth,
			locale: 'nl',
			onReady: [
				staticYearMonth,
				(): void => {
					fp.loadedPlugins.push('staticYearMonth');
				},
			],
		};
	};
}
